import AbstractProvidedClass from "../../shared/AbstractProvidedClass";
import Buttons from "./Fields/Buttons";
import InputField from "./Fields/InputField";
import MultiField from "./Fields/MultiField";
import SimpleField from "./Fields/SimpleField";
import SelectionOverview from "./Fields/SelectionOverview";
import SpeciesLabelList from "./Fields/ElementFields/SpeciesLabelList";
import AreaField from "./Fields/AreaField";
import SelectField from "./Fields/SelectField";
import DateField from "./Fields/DateField";
import EventTypes from "../../shared/EventTypes";
import { UPDATE_LAYER, UPDATE_DATE, MASS_UPDATE_DATE } from "../../tools/actions/ActionHelper";

export default class ElementInput extends AbstractProvidedClass {
    inputs = [];
    element = null;

    constructor(provider, tabContainer) {
        super(provider);

        this.container = this.p.createDiv()
            .parent(tabContainer)
            .style('height', (this.p.height - 20) + 'px')
            .class('pad-b');

        this.createTitle();
        this.createSpeciesLabels();
        this.createLayerSelector();
        this.createDateFields();
        this.createNotes();
        this.createDimensions();
        this.createSelectionLayerSelector();
        this.createSelectionDateFields();
        this.createSelected();
        this.createButtons();

        this.on(EventTypes.UPDATE_UNIT_SYSTEM, () => this.setElement(this.element));
        this.on(EventTypes.UPDATE_ELEMENT_LABELS, (element) => {
            if (element != this.element) return;
            this.title.populate(element);
        });
        this.on(EventTypes.UPDATE_ELEMENT_DATE, (element) => {
            if (element != this.element) return;
            this.addedDate.value(element.addedDate);
            this.removedDate.value(element.removedDate);
        });
        this.on(EventTypes.DATE_VALIDATION_ERROR, (element, field) => {
            if (element != this.element) return;
            let dateField = field === 'addedDate' ? this.addedDate : this.removedDate;
            dateField.showError('Removed date must be later than added');
        });
        this.on(EventTypes.UPDATE_ELEMENT_SHAPE, (element) => {
            if (element != this.element) return;
            this.setElement(element);
        });

        this.hide();
    }

    setElement(element) {
        this.element = element;
        if (!this.element) {
            this.drawMenu.hide();
            return;
        }
        this.selectionLayerContainer.hide();
        this.selectionDatesContainer.hide();
        this.dimHeader.show();
        this.dimContent.show();
        this.inputs.map((input) => input.setElement(element));
        if (this.selection.length > 1) {
            this.title.value(element.title + ' (' + this.selection.length + ')');
            this.multiInput.hide();
            let items = this.selection.items;
            [this.size, this.width, this.height].map((input) => {
                let same = true;
                let i = 0;
                while (same && items[i + 1]) {
                    same = items[i][input.attributeValue] === items[i + 1][input.attributeValue];
                    i++;
                }
                if (!same) {
                    input.value('');
                }
            });
        }
    }

    setDrawMode(type) {
        this.inputs.map((input) => input.setDrawMode(type));
    }

    reset() {
        this.inputs.map((input) => input.isInputField ? input.reset() : null);
    }

    registerInput(input) {
        input.parent(this._dimContainer || this.container)
        this.inputs.push(input);
        input.hide();
    }

    showSelection() {
        this.element = null;
        this.drawMenu.show('selection');
        this.drawMenu.setSelectionAppearance(this.selection.items);
        this.inputs.map((input) => input.hide());
        this.dimHeader.hide();
        this.dimContent.hide();
        this.title.value('Selection');
        this.title.show();
        this.populateSelectionLayer();
        this.selectionLayerContainer.show();
        this.populateSelectionDates();
        this.selectionDatesContainer.show();
        this.selected.update();
        this.selected.show();
        this.buttons.all();
        this.buttons.show();
    }

    showInfo() {
        if (this.element) {
            this.drawMenu.show('element');
            this.drawMenu.setAppearance(this.element.appearance);
            if (this.element.type === 'ellipse') {
                this.drawMenu.showCanopyStyleForElement(this.element);
            } else if (this.element.selectedSegment) {
                this.drawMenu.showSegmentTypeForSegment(this.element, this.element.selectedSegment);
                this.multiInput.highlightSegment(this.element.selectedSegment.index);
            } else {
                this.multiInput.unhighlightSegment();
            }
        }
    }

    createTitle() {
        this.title = SimpleField.setup(this.provider)
            .setType('h3')
            .setPopulate((element, field) => field.html(element.name))
            .style('margin-top', '0px')
            .make();
        this.registerInput(this.title);
    }

    createDateFields() {
        this.addedDate = DateField.setup(this.provider)
            .label('Added', true)
            .hideInDrawMode()
            .setPopulate((element, field) => field.value(element.addedDate))
            .onChange(UPDATE_DATE, 'addedDate', (value) => value)
            .make();
        this.registerInput(this.addedDate);

        this.removedDate = DateField.setup(this.provider)
            .label('Removed', true)
            .hideInDrawMode()
            .setPopulate((element, field) => field.value(element.removedDate))
            .onChange(UPDATE_DATE, 'removedDate', (value) => value)
            .make();
        this.registerInput(this.removedDate);
    }

    createNotes() {
        this.notes = AreaField.setup(this.provider)
            .hideInDrawMode()
            .make();
        this.registerInput(this.notes);
    }

    createDimensions() {
        this.dimHeader = this.p.createDiv()
            .parent(this.container)
            .style('display', 'flex')
            .style('align-items', 'center')
            .style('justify-content', 'space-between');
        this.p.createElement('h3', 'Dimensions')
            .class('input-label')
            .style('margin', '0')
            .parent(this.dimHeader);
        let toggleBtn = this.p.createButton('▾')
            .parent(this.dimHeader)
            .class('btn-icon-clear')
            .style('line-height', '1');

        this.dimContent = this.p.createDiv().parent(this.container);
        let collapsed = false;
        toggleBtn.mousePressed(() => {
            collapsed = !collapsed;
            this.dimContent.elt.style.display = collapsed ? 'none' : '';
            toggleBtn.html(collapsed ? '◂' : '▾');
        });

        this._dimContainer = this.dimContent;
        this.createAreaLength();
        this.createSize();
        this.createWidth();
        this.createHeight();
        this.createMultiInput();
        this._dimContainer = null;
    }

    createAreaLength() {
        this.areaField = SimpleField.setup(this.provider)
            .setType('p')
            .types(['rectangle', 'ellipse', 'polygon'])
            .setPopulate((element, field) => {
                const unit = element.settings.metric ? 'm²' : 'ft²';
                field.html(`Area: ${element.displayArea} ${unit}`);
            })
            .style('margin', '4px 0')
            .make();
        this.registerInput(this.areaField);

        this.lengthField = SimpleField.setup(this.provider)
            .setType('p')
            .types(['line'])
            .setPopulate((element, field) => {
                const unit = element.settings.metric ? 'm' : 'ft';
                field.html(`Total length: ${element.displayTotalLength} ${unit}`);
            })
            .style('margin', '4px 0')
            .make();
        this.registerInput(this.lengthField);
    }

    createSpeciesLabels() {
        this.speciesLabelList = SpeciesLabelList.setup(this.provider)
            .make();
        this.registerInput(this.speciesLabelList);
    }

    createLayerSelector() {
        this.layerSelector = SelectField.setup(this.provider)
            .label('Layer', true)
            .hideInDrawMode()
            .setOptions(() => [
                { value: '', label: 'None' },
                ...Object.values(this.layers.collection).map(
                    layer => ({ value: layer.id, label: layer.name })
                )
            ])
            .setPopulate((element, field) => {
                field.value(element.layer?.id ?? '');
            })
            .onChange(UPDATE_LAYER, (value) => this.layers.get(value))
            .make();
        this.registerInput(this.layerSelector);
        this.on(EventTypes.UPDATE_ELEMENT_LAYER, (elements, value) => {
            let arr = Array.isArray(elements) ? elements : [elements];
            if (arr.includes(this.element)) {
                this.layerSelector.value(value);
            }
        });
        let refreshLayerOptions = () => {
            if (this.element) {
                this.layerSelector.populateOptions();
                this.layerSelector.value(this.element.layer?.id ?? '');
            }
        };
        this.on(EventTypes.UPDATE_LAYER_NAME, refreshLayerOptions);
        this.on(EventTypes.ADD_LAYER, refreshLayerOptions);
        this.on(EventTypes.REMOVE_LAYER, refreshLayerOptions);
    }

    createSize() {
        this.size = InputField.setup(this.provider)
            .dimension()
            .id('element-size')
            .label('Size')
            .attribute('size')
            .style('margin-top', '5px')
            .setPopulate((element, field) => field.value(element.displaySize))
            .types(['ellipse'])
            .input((element, value) => {
                element.setSize(value);
                element.generateCanopyPoints();
            })
            .make();
        this.registerInput(this.size);
    }

    createWidth() {
        this.width = InputField.setup(this.provider)
            .dimension()
            .id('element-width')
            .label('Width')
            .attribute('width')
            .setPopulate((element, field) => field.value(element.displayWidth))
            .types(['rectangle'])
            .input((element, value) => element.setWidth(value))
            .make();
        this.registerInput(this.width);
    }

    createHeight() {
        this.height = InputField.setup(this.provider)
            .dimension()
            .id('element-height')
            .label('Height')
            .attribute('height')
            // .style('margin-top', '5px')
            .setPopulate((element, field) => field.value(element.displayHeight))
            .types(['rectangle'])
            .input((element, value) => element.setHeight(value))
            .make();
        this.registerInput(this.height);
    }

    createMultiInput() {
        this.multiInput = MultiField.setup(this.provider)
            .class('input-group')
            // .style('margin-top', '5px')
            .types(['polygon', 'line'])
            .make();
        this.registerInput(this.multiInput);
    }

    createSelectionLayerSelector() {
        this.selectionLayerContainer = this.p.createDiv().parent(this.container);

        this.p.createDiv('Layer')
            .parent(this.selectionLayerContainer)
            .class('input-label');

        this.selectionLayerSelect = this.p.createElement('select')
            .parent(this.selectionLayerContainer)
            .class('input-text');

        this.selectionLayerSelect.elt.onchange = () => {
            let value = this.selectionLayerSelect.value();
            if (value === '__mixed__') return;
            let layer = value ? this.layers.get(value) : null;
            this.actions.execute(UPDATE_LAYER, this.selection.items, layer);
        };

        this.on(EventTypes.UPDATE_ELEMENT_LAYER, (elements, value) => {
            if (this.selectionLayerContainer.elt.style.display !== 'none') {
                this.populateSelectionLayer();
            }
        });

        let refreshSelectionLayer = () => {
            if (this.selectionLayerContainer.elt.style.display !== 'none') {
                this.populateSelectionLayer();
            }
        };
        this.on(EventTypes.UPDATE_LAYER_NAME, refreshSelectionLayer);
        this.on(EventTypes.ADD_LAYER, refreshSelectionLayer);
        this.on(EventTypes.REMOVE_LAYER, refreshSelectionLayer);

        this.selectionLayerContainer.hide();
    }

    populateSelectionLayer() {
        let select = this.selectionLayerSelect.elt;
        select.innerHTML = '';

        let items = this.selection.items;
        let layers = items.map(el => el.layer);
        let allSame = layers.every(l => l === layers[0]);

        if (!allSame) {
            let mixed = document.createElement('option');
            mixed.value = '__mixed__';
            mixed.textContent = '';
            select.appendChild(mixed);
        }

        let none = document.createElement('option');
        none.value = '';
        none.textContent = 'None';
        select.appendChild(none);

        Object.values(this.layers.collection).forEach(layer => {
            let option = document.createElement('option');
            option.value = layer.id;
            option.textContent = layer.name;
            select.appendChild(option);
        });

        if (allSame) {
            select.value = layers[0]?.id ?? '';
        } else {
            select.value = '__mixed__';
        }
    }

    createSelectionDateFields() {
        this.selectionDatesContainer = this.p.createDiv().parent(this.container);

        this.selectionAddedDate = DateField.setup(this.provider)
            .label('Added')
            .onInput((value) => {
                this.actions.execute(MASS_UPDATE_DATE, this.selection.items, 'addedDate', value);
            })
            .make();
        this.selectionAddedDate.field.parent(this.selectionDatesContainer);

        this.selectionRemovedDate = DateField.setup(this.provider)
            .label('Removed')
            .onInput((value) => {
                this.actions.execute(MASS_UPDATE_DATE, this.selection.items, 'removedDate', value);
            })
            .make();
        this.selectionRemovedDate.field.parent(this.selectionDatesContainer);

        this.selectionDatesContainer.hide();
    }

    populateSelectionDates() {
        let items = this.selection.items;

        let addedDates = items.map(el => el.addedDate ?? null);
        let allAddedSame = addedDates.every(d => d === addedDates[0]);
        this.selectionAddedDate.value(allAddedSame ? addedDates[0] : null);

        let removedDates = items.map(el => el.removedDate ?? null);
        let allRemovedSame = removedDates.every(d => d === removedDates[0]);
        this.selectionRemovedDate.value(allRemovedSame ? removedDates[0] : null);
    }

    createSelected() {
        this.selected = SelectionOverview.setup(this.provider)
            .make();
        this.registerInput(this.selected);
    }

    createButtons() {
        this.buttons = Buttons.setup(this.provider)
            .make();
        this.registerInput(this.buttons);
    }

    show() {
        this.container.show();
    }

    hide() {
        this.container.hide();
    }
}
